import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { fetchStores } from "../store/actions/storeActions";
import { getAllCoupons } from "../store/actions/couponActions";
import { fetchTrendingSearchTerms } from "../store/actions/searchActions";
import { getCategories } from "../store/actions/categoryActions";
import { buildCategoryChips } from "../utils/categoryChips";
import HomeHero from "../components/home/HomeHero";
import HomeFeaturedDeals from "../components/home/HomeFeaturedDeals";
import HomePopularStores from "../components/home/HomePopularStores";
import HomeTrendingSearches from "../components/home/HomeTrendingSearches";
import HomeLatestCoupons from "../components/home/HomeLatestCoupons";
import HomeCategoryBrowse from "../components/home/HomeCategoryBrowse";
import HomeFaqSection from "../components/home/HomeFaqSection";
import NewsletterBanner from "../components/shared/NewsletterBanner";
import useSEO from "../hooks/useSEO";
import seoConfig from "../config/seoConfig";
import {
  generateOrganizationSchema,
  generateWebSiteSchema,
  generateFAQSchema,
} from "../utils/schemaGenerators";
import { homeFaqs } from "../data/homeFaqs";

const Wrap = styled.div`
  flex: 1;
`;

const LoadNote = styled.p`
  text-align: center;
  padding: 0.5rem 1rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  font-size: 0.875rem;
`;

const ErrorNote = styled(LoadNote)`
  color: hsl(${({ theme }) => theme.colors.destructive || "0 84% 60%"});
`;

export default function HomePage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reduxStores = useSelector((s) => s.store.stores);
  const reduxCoupons = useSelector((s) => s.coupon.coupons);
  const reduxCategories = useSelector((s) => s.category.categories);
  const storeLoading = useSelector((s) => s.store.loading);
  const couponLoading = useSelector((s) => s.coupon.loading);
  const storeError = useSelector((s) => s.store.error);
  const couponError = useSelector((s) => s.coupon.error);
  const trendingTerms = useSelector((s) => s.search.trendingTerms);
  const trendLoading = useSelector((s) => s.search.trendingLoading);
  const trendError = useSelector((s) => s.search.trendingError);

  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    dispatch(fetchStores());
    dispatch(getAllCoupons());
    dispatch(fetchTrendingSearchTerms());
    dispatch(getCategories());
  }, [dispatch]);

  const stores = reduxStores || [];
  const couponList = reduxCoupons || [];
  const categoryChips = useMemo(
    () => buildCategoryChips(reduxCategories, reduxStores),
    [reduxCategories, reduxStores]
  );
  const termsForHome = trendingTerms || [];

  const loading = storeLoading || couponLoading || trendLoading;
  const catalogError = storeError || couponError || trendError;

  const filteredStores = useMemo(() => {
    if (selectedCategory === "All") return stores;
    return stores.filter((s) => s.category === selectedCategory);
  }, [selectedCategory, stores]);

  const seo = useSEO({
    schema: [
      generateOrganizationSchema({
        name: seoConfig.siteName,
        url: seoConfig.siteUrl,
      }),
      generateWebSiteSchema({
        name: seoConfig.siteName,
        url: seoConfig.siteUrl,
        searchUrl: `${seoConfig.siteUrl}/search`,
      }),
      generateFAQSchema(
        homeFaqs.map((f) => ({ question: f.q, answer: f.a }))
      ),
    ].filter(Boolean),
  });

  return (
    <Wrap>
      {seo}
      {loading && <LoadNote>Loading latest deals…</LoadNote>}
      {!loading && catalogError && (
        <ErrorNote>
          {t("common.catalogError")}
        </ErrorNote>
      )}
      <HomeHero />
      <HomeFeaturedDeals coupons={couponList} />
      <HomePopularStores filteredStores={filteredStores} />
      <HomeTrendingSearches terms={termsForHome} />
      <HomeLatestCoupons coupons={couponList} />
      <HomeCategoryBrowse
        categories={categoryChips}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        filteredStores={filteredStores}
      />
      <HomeFaqSection />
      <NewsletterBanner />
    </Wrap>
  );
}
